import { isEqual, sortBy } from 'lodash'
import type { Ref } from 'vue'
import type { BuilderTab } from './types'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { CreatePassportTabGroupBody } from './useTabPersistence'

type TabPersistenceApi = {
  createTabGroup: (body: CreatePassportTabGroupBody) => Promise<number>
  updateTabGroup: (
    group: ObjectTypeGroup,
    patch: Partial<Pick<ObjectTypeGroup, 'name'>>,
    catalogTypeFallback: number,
  ) => Promise<void>
  reorderTabGroups: (groupId: number, targetGroupId: number) => Promise<void>
  deleteTabGroup: (id: number) => Promise<void>
}

export type PassportTabManagerPersistApi = {
  tabs: Ref<BuilderTab[]>
  pendingDeletedServerGroupIds: Ref<number[]>
  promotePendingTab: (clientTabId: string, newGroupId: number) => void
}

/**
 * Persists tab strip changes via manuals APIs before the main object-type PUT.
 * Order: DELETE removed groups → refetch → POST new tabs (left-to-right) → refetch → PUT renames
 * → refetch → PUT reorder until server tab order matches UI → refetch.
 */
export async function runPassportTabSavePipeline(opts: {
  persistence: TabPersistenceApi
  catalogTypeId: number
  fieldGroups: Ref<ObjectTypeGroup[]>
  refetch: () => Promise<unknown>
  tabManager: PassportTabManagerPersistApi
}): Promise<void> {
  const { persistence, catalogTypeId, fieldGroups, refetch, tabManager } = opts

  const toDelete = [...tabManager.pendingDeletedServerGroupIds.value]
  for (const id of toDelete) {
    await persistence.deleteTabGroup(id)
  }
  tabManager.pendingDeletedServerGroupIds.value = []
  await refetch()

  const pendingTabs = tabManager.tabs.value.filter(
    (t) => !t.isSynthetic && t.groupId == null,
  ) as BuilderTab[]

  for (const t of pendingTabs) {
    const newId = await persistence.createTabGroup({
      catalog_invent_objects_type_id: catalogTypeId,
      name: t.label,
      open: true,
      position: 'APPEND',
      mode: 'EDITABLE',
      is_tab: true,
      type: 'GROUP',
      list_invent_object_type_id: null,
    })
    tabManager.promotePendingTab(t.id, newId)
  }

  if (pendingTabs.length) await refetch()

  const serverTabs = tabManager.tabs.value.filter((t) => !t.isSynthetic && t.groupId != null)
  for (const t of serverTabs) {
    const g = fieldGroups.value.find((x) => x.id === t.groupId)
    if (g && t.label !== g.name) {
      await persistence.updateTabGroup(g, { name: t.label }, catalogTypeId)
    }
  }
  await refetch()

  const desiredIds = serverTabs.map((t) => t.groupId!)
  await convergeTabOrder(desiredIds, persistence.reorderTabGroups, refetch, () =>
    readTabGroupOrder(fieldGroups.value),
  )

  await refetch()
}

function readTabGroupOrder(groups: ObjectTypeGroup[]): number[] {
  return sortBy(
    groups.filter((g) => g.is_tab),
    (g) => g.sort ?? 0,
  ).map((g) => g.id)
}

async function convergeTabOrder(
  desiredIds: number[],
  reorder: (groupId: number, targetGroupId: number) => Promise<void>,
  refetch: () => Promise<unknown>,
  readOrder: () => number[],
): Promise<void> {
  const maxIter = desiredIds.length * desiredIds.length + 8
  for (let k = 0; k < maxIter; k++) {
    await refetch()
    const cur = readOrder()
    if (isEqual(cur, desiredIds)) return
    let i = 0
    while (i < desiredIds.length && cur[i] === desiredIds[i]) i++
    if (i >= desiredIds.length) return
    const want = desiredIds[i]!
    const target = cur[i]!
    await reorder(want, target)
  }
}
